import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Box,
  Checkbox,
  Typography,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material';
import EmptyState from '../common/EmptyState';

/**
 * Reusable data table component with sorting, pagination, and selection
 */
const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  // Pagination
  pagination = true,
  page = 0,
  rowsPerPage = 10,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  // Sorting
  sortable = true,
  orderBy,
  order = 'asc',
  onSort,
  // Selection
  selectable = false,
  selected = [],
  onSelect,
  onSelectAll,
  // Actions
  rowActions,
  onRowClick,
  // Styling
  stickyHeader = false,
  maxHeight,
  size = 'medium',
  emptyMessage = 'No data available',
  emptyIcon,
  sx = {},
}) => {
  const handleSort = (columnId) => {
    if (!sortable || !onSort) return;
    const isAsc = orderBy === columnId && order === 'asc';
    onSort(columnId, isAsc ? 'desc' : 'asc');
  };

  const handleSelectAll = (event) => {
    if (!onSelectAll) return;
    if (event.target.checked) {
      onSelectAll(data.map((row) => row.id));
    } else {
      onSelectAll([]);
    }
  };

  const handleSelectRow = (id) => {
    if (!onSelect) return;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    onSelect(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const renderSkeletonRows = () => {
    return Array.from({ length: rowsPerPage }).map((_, index) => (
      <TableRow key={index}>
        {selectable && (
          <TableCell padding="checkbox">
            <Skeleton variant="rectangular" width={24} height={24} />
          </TableCell>
        )}
        {columns.map((column) => (
          <TableCell key={column.id}>
            <Skeleton variant="text" />
          </TableCell>
        ))}
        {rowActions && (
          <TableCell>
            <Skeleton variant="circular" width={32} height={32} />
          </TableCell>
        )}
      </TableRow>
    ));
  };

  const renderCellContent = (row, column) => {
    if (column.render) {
      return column.render(row[column.id], row);
    }
    return row[column.id];
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', ...sx }}>
      <TableContainer sx={{ maxHeight: maxHeight }}>
        <Table stickyHeader={stickyHeader} size={size}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < data.length
                    }
                    checked={data.length > 0 && selected.length === data.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth, width: column.width }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {sortable && column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {rowActions && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              renderSkeletonRows()
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)
                  }
                >
                  <EmptyState
                    title={emptyMessage}
                    description=""
                    icon={emptyIcon}
                    sx={{ py: 4 }}
                  />
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <TableRow
                    hover
                    key={row.id || index}
                    onClick={() => onRowClick && onRowClick(row)}
                    role={selectable ? 'checkbox' : undefined}
                    aria-checked={selectable ? isItemSelected : undefined}
                    selected={isItemSelected}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRow(row.id);
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell key={column.id} align={column.align || 'left'}>
                        {renderCellContent(row, column)}
                      </TableCell>
                    ))}
                    {rowActions && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {rowActions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && !loading && data.length > 0 && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalRows ?? data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </Paper>
  );
};

export default DataTable;
